import type { Tables } from "@/integrations/supabase/types";

type DealDocumentRow = Tables<"deal_documents">;

export type ValidationStatus = "strong" | "partial" | "weak" | "failed";

export interface DocumentConfidence {
  level: string;
  score: number;
  notes: string[];
}

export interface NormalizedDocumentIntelligence {
  document_id: string;
  doc_type: string;
  document_lane: string | null;
  document_subtype: string | null;
  risk_flags: unknown[];
  recommended_underwriting_adjustments: unknown[];
  confidence: DocumentConfidence;
  executive_interpretation: string;
  field_mapping: Record<string, unknown>;
  evidence_log: unknown[];
  extracted_data: Record<string, unknown>;
  validation_status: ValidationStatus | null;
  validation_summary: Record<string, unknown> | null;
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asConfidence(value: unknown): DocumentConfidence {
  const confidence = asObject(value);
  return {
    level: asString(confidence.level) || "low",
    score: typeof confidence.score === "number" ? confidence.score : 0,
    notes: asArray(confidence.notes).map((note) => String(note)),
  };
}

/**
 * Canonical OCR adapter.
 *
 * Consumers should read from the promoted deal_documents columns first so UI and
 * orchestration behave consistently across document types. We still fall back to
 * the stored envelope for legacy rows or partially populated records.
 */
export function normalizeDocumentIntelligence(
  document: Partial<DealDocumentRow> | null | undefined,
): NormalizedDocumentIntelligence | null {
  if (!document?.id) return null;

  const envelope = asObject(document.extracted_data);
  const envelopeConfidence = asConfidence(envelope.confidence);

  return {
    document_id: document.id,
    doc_type: document.doc_type ?? "unknown",
    document_lane: document.document_lane ?? (asString(envelope.document_lane) || null),
    document_subtype: document.document_subtype ?? (asString(envelope.document_subtype) || null),
    risk_flags: asArray(document.risk_flags).length > 0 ? asArray(document.risk_flags) : asArray(envelope.risk_flags),
    recommended_underwriting_adjustments:
      asArray(document.recommended_underwriting_adjustments).length > 0
        ? asArray(document.recommended_underwriting_adjustments)
        : asArray(envelope.recommended_underwriting_adjustments),
    confidence: asArray(document.confidence ? [document.confidence] : []).length > 0
      ? asConfidence(document.confidence)
      : envelopeConfidence,
    executive_interpretation:
      asString(document.executive_interpretation) || asString(envelope.executive_interpretation),
    field_mapping:
      Object.keys(asObject(document.brickshare_field_mapping)).length > 0
        ? asObject(document.brickshare_field_mapping)
        : asObject(envelope.brickshare_field_mapping),
    evidence_log: asArray(document.evidence_log).length > 0 ? asArray(document.evidence_log) : asArray(envelope.evidence_log),
    extracted_data: asObject(envelope.extracted_data),
    validation_status: ["strong", "partial", "weak", "failed"].includes(asString(envelope.validation_status))
      ? (asString(envelope.validation_status) as ValidationStatus)
      : null,
    validation_summary: Object.keys(asObject(envelope.validation_summary)).length > 0
      ? asObject(envelope.validation_summary)
      : null,
  };
}

export function buildDocumentIntelligenceContext(
  documents: Array<Partial<DealDocumentRow>>,
): {
  documents: NormalizedDocumentIntelligence[];
  by_lane: Record<string, NormalizedDocumentIntelligence>;
} {
  const normalized = documents
    .map((document) => normalizeDocumentIntelligence(document))
    .filter((document): document is NormalizedDocumentIntelligence => Boolean(document));

  const byLane = normalized.reduce<Record<string, NormalizedDocumentIntelligence>>((acc, document) => {
    const key = document.document_lane || document.doc_type;
    if (!acc[key]) acc[key] = document;
    return acc;
  }, {});

  return {
    documents: normalized,
    by_lane: byLane,
  };
}